import { Field, useFormikContext } from "formik";
import React, { FC, useState } from "react";
import { toast } from "react-toastify";
import Togglebox from "../../components/togglebox/togglebox";
import { COMMAND_STATE } from '../../constants/command-status';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { executeCommand } from '../../services/executeCommandService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { validateItemName, validatePassword } from "../../utilities/Validation";
import { useDialog } from "../dialog/DialogContext";
import Label from "../label/label";

interface AccountProps {
    isLicensed: boolean,
    currentItem: any,
    isAccountPopulated: boolean, // Whether all the required details are populated to allow querying
}

const MobileAccountCard: FC<AccountProps> = ({ isLicensed, currentItem, isAccountPopulated }) => {
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const { dirty } = useFormikContext<any>();

    const errorHandler = (accessor) => {
        const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }
            , errors as any);
        if (error) {
            return <span className='text-red-600 text-xs'>{error}</span>
        } else {
            return null;
        }
    };

    /**
    * The account is tested using saved account details only. Therefore, to test the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * @returns 
    */
    const canTestAccount = () => {
        return isLicensed && isAccountPopulated && !dirty;
    }

    const onEnabled = () => {
        const newEnabledState = !values.enabled;

        if (newEnabledState && !isLicensed) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unlicensed), message: translate.byKey(TranslationKeys.unlicensedFeature) });
            setFieldValue("enabled", false);
            return;
        }

        setFieldValue("enabled", newEnabledState);
    }

    const onValidatePassword = (value: string) => {

        // Ignores validation only if password is already captured and no new edits are made
        if (currentItem?.isPasswordCaptured && !value) {
            return null;
        }

        // If this is either the initial capture of the password or an edit, validate it
        return validatePassword(value, translate, currentItem, 2);
    }

    const onTestAccount = () => {
        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: AccountSubCmds.Test_Account,
            entityId: currentItem.id,
            jsonResponse: ""
        }

        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                if (values.enabled === false) {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToDisabledAccount));
                }
                else {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToAccount));
                }
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    return (
        <div className="w-full p-2 inline-flex justify-start items-top bg-white">

            {/* Left-hand column */}
            <div className="flex flex-row py-4 px-8">
                <div className="flex flex-col">
                    <div className="mb-1">
                        <div className="flex flex-row justify-between" id="organizationLabel">
                            <Label
                                htmlFor="details.organizationId.value"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.organizationId)}:</Label>
                            {errorHandler("details.organizationId.value")}
                        </div>
                        <Field id='organizationInputFieldId'
                            type="text"
                            name="details.organizationId.value"
                            autoFocus
                            className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => validateItemName(value, translate)} />
                    </div>

                    <div className="mb-1">
                        <div className="flex flex-row justify-between" id="userIdLabel">
                            <Label
                                htmlFor="details.userId.value"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.userId)}:</Label>
                            {errorHandler("details.userId.value")}
                        </div>

                        <Field id='userIdInputFieldId'
                            name="details.userId.value"
                            className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => validateItemName(value, translate)} />
                    </div>

                    <div>
                        <div className="flex flex-row justify-between" id="passwordLabel">
                            <Label
                                htmlFor="tempPassword"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.password)}:</Label>
                            {errorHandler("tempPassword")}
                        </div>
                        <Field id='passwordInputFieldId'
                            type={'password'}
                            name="tempPassword"
                            className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => onValidatePassword(value)}
                            placeholder={(currentItem?.isPasswordCaptured) ? "********" : translate.byKey(TranslationKeys.enterNewPassword)} />
                    </div>
                </div>
            </div>

            {/* Right-hand column */}
            <div className="flex flex-row py-4 px-8">
                <div className="flex flex-col">
                    {/* <div className="mb-1">
                        <div className="flex flex-row justify-between" id="userIdLabel">
                            <Label
                                htmlFor="details.userId.value"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.userId)}:</Label>
                            {errorHandler("details.userId.value")}
                        </div>

                        <Field id='userIdInputFieldId'
                            name="details.userId.value"
                            className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => validateItemName(value, translate)} />
                    </div> */}

                    <div className="mb-2">
                        <div className="flex flex-row justify-between mb-1" id="enabled" >
                            <Label htmlFor="enabled" className="text-xs text-gray-600 mt-1">
                                {translate.byKey(TranslationKeys.enabled)}:
                            </Label>
                        </div>
                        <div className="flex flex-row justify-between">
                            <Togglebox name="enabled" onChanged={onEnabled} label={""} />
                        </div>
                    </div>

                    <div className="mb-2 mt-1">
                        <button
                            type='button'
                            className={!canTestAccount()
                                ? " secondary secondary-disabled cursor-not-allowed rounded-lg shadow-lg h-8 px-2 mt-1"
                                : " h-8 px-2 mt-1 secondary bg-sidemenu-focus text-white rounded-lg shadow-lg"}
                            disabled={!canTestAccount()}
                            onClick={onTestAccount}
                            id="testQueryButton">
                            <span className="mr-2">{translate.byKey(TranslationKeys.testAccount)}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MobileAccountCard;
